import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, ExternalLink, Check, X, ChevronRight } from "lucide-react";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../components/SEO';

// Modal detail content for all 8 items
const capabilityDetails = [
  {
    id: 0,
    eyebrow: "Foundation",
    title: "Built from the ground up",
    shortDesc: "Custom platforms designed and developed from scratch — no templates, no shortcuts.",
    content: [
      "Every platform we build starts with your specific business model, user needs, and operational goals. We don't retrofit generic templates — we design custom solutions from day one.",
      "This approach means your platform works the way your business works, with workflows tailored to how you actually operate."
    ],
    bullets: [
      "Tailored user experiences for your audience",
      "Custom workflows that match your operations",
      "Flexible architecture that grows with you",
      "No unnecessary bloat or unused features"
    ]
  },
  {
    id: 1,
    eyebrow: "Control",
    title: "Custom dashboards & admin systems",
    shortDesc: "Full control panels for managing users, content, orders, and operations in real time.",
    content: [
      "Your team needs visibility and control. We build intuitive admin dashboards that put everything at your fingertips — user management, order tracking, content updates, and operational insights.",
      "No more juggling spreadsheets or third-party tools. Everything lives in one place, designed specifically for how your business runs."
    ],
    bullets: [
      "Real-time order and user management",
      "Content management without technical knowledge",
      "Role-based access for your team",
      "Actionable insights and reporting"
    ]
  },
  {
    id: 2,
    eyebrow: "Security",
    title: "Secure file storage & delivery",
    shortDesc: "Handle large uploads, media storage, and protected file access with reliable cloud infrastructure.",
    content: [
      "Whether you're handling client deliverables, user uploads, or media libraries, we build secure, scalable file systems that protect your assets and make delivery seamless.",
      "Files are stored securely in the cloud with proper access controls, organized structures, and reliable delivery — even for large files."
    ],
    bullets: [
      "Protected uploads with access control",
      "Organized file management systems",
      "Secure client delivery workflows",
      "Scalable cloud storage infrastructure"
    ]
  },
  {
    id: 3,
    eyebrow: "Reliability",
    title: "Performance & scalability focused",
    shortDesc: "Built to handle real users, real traffic, and long-term growth without breaking.",
    content: [
      "A platform that works in testing but fails under real usage isn't a platform — it's a liability. We build for production from the start, with performance and scalability baked into every decision.",
      "Your platform should handle growth gracefully, whether that's 100 users or 10,000."
    ],
    bullets: [
      "Optimized for real-world traffic patterns",
      "Clean, maintainable architecture",
      "Built for long-term reliability",
      "Scales with your business growth"
    ]
  },
  {
    id: 4,
    eyebrow: "Production",
    title: "Real users. Real transactions.",
    shortDesc: "Live platforms actively handling bookings, payments, and user activity.",
    content: [
      "We don't build demos or prototypes that sit on a shelf. Every platform we deliver is production-ready — designed to handle real customers, real transactions, and real business operations from day one.",
      "Our work is proven in the field, with live platforms actively serving users and processing real activity."
    ],
    bullets: [
      "Production-ready from launch",
      "Battle-tested with real customers",
      "Active bookings and transactions",
      "Ongoing reliability and support"
    ]
  },
  {
    id: 5,
    eyebrow: "Revenue",
    title: "Payments & subscriptions",
    shortDesc: "Integrated payment systems with support for one-time payments, subscriptions, and platform-based payouts.",
    content: [
      "Revenue is the lifeblood of your platform. We integrate robust payment systems that handle one-time purchases, recurring subscriptions, platform fees, and payouts — all with the security and reliability your business demands.",
      "From simple checkout flows to complex marketplace payments, we build the financial infrastructure your platform needs."
    ],
    bullets: [
      "One-time and recurring payment support",
      "Subscription management and billing",
      "Platform fees and payout systems",
      "Secure checkout experiences"
    ]
  },
  {
    id: 6,
    eyebrow: "Efficiency",
    title: "Automation & notifications",
    shortDesc: "Email, SMS, and system-triggered workflows to keep users informed and operations running smoothly.",
    content: [
      "Manual processes don't scale. We build intelligent automation that handles routine tasks, keeps users informed, and ensures nothing falls through the cracks.",
      "From booking confirmations to payment receipts to status updates — your platform communicates proactively so you don't have to."
    ],
    bullets: [
      "Triggered email and SMS notifications",
      "Automated status updates and reminders",
      "Workflow automation for operations",
      "Reduced manual intervention"
    ]
  },
  {
    id: 7,
    eyebrow: "Connectivity",
    title: "Connected integrations",
    shortDesc: "Seamlessly integrates with tools like payment providers, cloud storage, messaging services, and analytics platforms.",
    content: [
      "Your platform doesn't exist in isolation. We build seamless connections to the services your business relies on — payment processors, cloud storage, messaging systems, analytics tools, and more.",
      "These integrations work reliably behind the scenes, giving you the power of best-in-class tools without the complexity."
    ],
    bullets: [
      "Payment provider integrations",
      "Cloud storage and file services",
      "Email and messaging systems",
      "Analytics and monitoring tools"
    ]
  }
];

const WebsiteDesign = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!modalOpen) return;
    
    if (e.key === 'Escape') {
      setModalOpen(false);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % capabilityDetails.length);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + capabilityDetails.length) % capabilityDetails.length);
    }
  }, [modalOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalOpen, handleKeyDown]);

  const openModal = (index) => {
    setActiveIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % capabilityDetails.length);
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + capabilityDetails.length) % capabilityDetails.length);
  };

  const currentItem = capabilityDetails[activeIndex];
  const isLastItem = activeIndex === capabilityDetails.length - 1;

  // Card items for display
  const leftColumnItems = capabilityDetails.slice(0, 4);
  const rightColumnItems = capabilityDetails.slice(4, 8);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO */}
      <SEO
        title="Custom Website Design & Development | RC Studios"
        description="We design and build custom platforms — not templates. Real-world applications with dashboards, booking systems, and automation. View our work."
        path="/services/website-design"
        jsonLd={[
          getServiceSchema({
            name: 'Custom Website Design & Development',
            description: 'Custom platform development including dashboards, booking systems, e-commerce, and automation.',
            url: 'https://rcstudios.net/services/website-design',
            areaServed: 'United States',
            priceRange: '$2,500+'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: 'Website Design', path: '/services/website-design' }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-[#050816] text-white pt-20 pb-16">
        <div className="container mx-auto px-6 max-w-[1000px] text-center">
          <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-4">Web Development</p>
          <h1 className="font-serif font-semibold text-4xl md:text-5xl lg:text-6xl mb-6">
            Custom Platforms.<br />Not Templates.
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            We design and build real-world platforms — not cookie-cutter websites.
          </p>
        </div>
      </section>

      {/* Featured Project Section - Editorial Design */}
      <section className="py-24 bg-gradient-to-b from-[#FAF9F6] to-white overflow-hidden">
        <div className="container mx-auto px-6 max-w-[1300px]">
          {/* Editorial Header */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
            {/* Left: Story */}
            <div className="order-2 lg:order-1">
              <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.2em] mb-4">Featured Project — Built by RC Studios</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#050816] mb-6 leading-tight">
                KasPaws
              </h2>
              <p className="text-[#050816]/80 text-xl leading-relaxed mb-6">
                Find verified pet care with confidence.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                KasPaws connects pet parents with reviewed local pet professionals for dog sitting, cat sitting, walks, visits, and photo updates. Every Pet Pro is verified before they can offer care — giving you peace of mind when booking trusted local sitters.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#C6A14A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#C6A14A]" />
                  </div>
                  <span className="text-[#050816]/70 text-sm">Real-time booking system with calendar sync</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#C6A14A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#C6A14A]" />
                  </div>
                  <span className="text-[#050816]/70 text-sm">Integrated payments and payout management</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#C6A14A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#C6A14A]" />
                  </div>
                  <span className="text-[#050816]/70 text-sm">Lost pet alerts with community notification</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#C6A14A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#C6A14A]" />
                  </div>
                  <span className="text-[#050816]/70 text-sm">Native mobile experience with responsive web app</span>
                </div>
              </div>
              <a 
                href="https://kaspaws.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  className="bg-[#050816] text-white hover:bg-[#1a2636] hover:shadow-lg rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all duration-200"
                  data-testid="cta-view-live"
                >
                  Visit KasPaws.com
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

            {/* Right: Desktop Screenshot - Floating */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative z-10">
                <div className="rounded-2xl shadow-2xl overflow-hidden border border-gray-200/50 transform lg:translate-x-8 hover:shadow-3xl transition-shadow duration-300">
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/9d2j6otv_image.png"
                    alt="KasPaws Desktop - Find verified pet care with confidence"
                    className="w-full h-auto"
                  />
                </div>
              </div>
              {/* Decorative background element */}
              <div className="absolute -top-4 -right-4 w-full h-full bg-[#C6A14A]/10 rounded-2xl -z-0 hidden lg:block" />
            </div>
          </div>

          {/* Mobile App Showcase */}
          <div className="relative bg-[#050816] rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] to-[#050816]" />
            
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              {/* Mobile Screenshot */}
              <div className="flex justify-center md:justify-start order-2 md:order-1">
                <div className="relative">
                  {/* Phone frame glow effect */}
                  <div className="absolute inset-0 bg-[#F5C056]/20 blur-3xl rounded-full transform scale-75" />
                  <div className="relative rounded-[2.5rem] shadow-2xl overflow-hidden max-w-[280px] border-4 border-[#1a2636]">
                    <img
                      src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/qy5xzlge_image.png"
                      alt="KasPaws Mobile App - Sign in screen with dog illustration"
                      className="w-full h-auto"
                  />
                  </div>
                </div>
              </div>

              {/* Mobile App Copy */}
              <div className="order-1 md:order-2 text-center md:text-left">
                <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-3">Mobile Experience</p>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                  Built by a Pet Sitter.<br />Trusted by Pet Parents.
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  The KasPaws mobile app delivers the full platform experience — from finding trusted care to managing your pet sitting business. Walking, boarding, pet alerts, and community features all in your pocket.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                    <span className="text-[#F5C056] text-sm">🐕</span>
                    <span className="text-white/80 text-sm">Walking</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                    <span className="text-[#F5C056] text-sm">🏠</span>
                    <span className="text-white/80 text-sm">Boarding</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                    <span className="text-[#F5C056] text-sm">📍</span>
                    <span className="text-white/80 text-sm">Pet Alerts</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                    <span className="text-[#F5C056] text-sm">👥</span>
                    <span className="text-white/80 text-sm">Community</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Migration Showcase - Ohh My Dog */}
          <div className="relative bg-gradient-to-br from-[#1a365d] to-[#0a1628] rounded-3xl p-8 md:p-12 overflow-hidden mt-8">
            <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
              {/* Copy */}
              <div className="text-center md:text-left">
                <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-3">Platform Migration</p>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                  Ohh My Dog
                </h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Private pet sitting and dog boarding in Sarasota & Lakewood Ranch. Fear-free pet care with verified credentials.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  We migrated this established pet care business from Manus to Vercel, then deployed the final production site on Emergent — ensuring zero downtime and improved performance throughout the transition.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start mb-6">
                  <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                    <span className="text-gray-400 text-xs line-through">Manus</span>
                    <span className="text-white/60 text-xs">→</span>
                    <span className="text-gray-400 text-xs line-through">Vercel</span>
                    <span className="text-white/60 text-xs">→</span>
                    <span className="text-[#F5C056] text-xs font-medium">Emergent</span>
                  </div>
                </div>
                <a 
                  href="https://ohhmydogsrq.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button 
                    className="bg-white/10 text-white hover:bg-white/20 border border-white/20 rounded-lg h-11 px-6 text-sm uppercase tracking-widest font-medium transition-all duration-200"
                    data-testid="cta-visit-ohhmydog"
                  >
                    Visit Site
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>

              {/* Screenshot */}
              <div className="flex justify-center">
                <div className="rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-w-[500px]">
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/3bz10w9l_image.png"
                    alt="Ohh My Dog - Private Pet Service website"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Behind the Platform - KP OPS Section */}
          <div className="relative bg-gradient-to-br from-[#C6A14A]/10 to-[#0a1628]/5 rounded-3xl p-8 md:p-12 overflow-hidden mt-8 border border-[#C6A14A]/20">
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              {/* Copy */}
              <div className="text-center md:text-left">
                <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.2em] mb-3">Behind the Platform</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[#050816] mb-4">
                  More Than Customer-Facing Websites
                </h3>
                <p className="text-[#050816]/70 text-sm leading-relaxed mb-4">
                  For KasPaws, RC Studios also built <strong>KP OPS</strong> — a private operational workspace used to organize research, review contacts, prepare personalized communications, require exact approval before sending, monitor replies, and report results.
                </p>
                <p className="text-[#050816]/60 text-sm leading-relaxed mb-6">
                  If your business needs both a customer-facing platform and internal systems to support operations, RC Studios can design and build both.
                </p>
                <Link 
                  to="/services/ai-systems-automation"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                  data-testid="kpops-link"
                >
                  Learn about AI Systems & Automation
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {/* KP OPS Screenshot */}
              <div className="flex justify-center">
                <div className="rounded-2xl shadow-xl overflow-hidden border border-[#050816]/10 max-w-[400px]">
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/lbi5i5zq_image.png"
                    alt="KP OPS - Secure Operations Center"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Build Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-[1000px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#050816] mb-4">What We Build</h2>
            <p className="text-muted-foreground">Click any capability to learn more</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {leftColumnItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => openModal(item.id)}
                  className="w-full text-left flex gap-4 p-5 bg-[#FAF9F6] rounded-xl hover:bg-[#f0efe9] hover:shadow-md transition-all duration-200 group cursor-pointer border border-transparent hover:border-[#C6A14A]/30"
                  data-testid={`capability-card-${item.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#C6A14A]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C6A14A]/20 transition-colors">
                    <Check className="w-4 h-4 text-[#C6A14A]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#050816] font-semibold mb-1 group-hover:text-[#C6A14A] transition-colors">{item.title}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.shortDesc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#C6A14A] transition-colors flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {rightColumnItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => openModal(item.id)}
                  className="w-full text-left flex gap-4 p-5 bg-[#FAF9F6] rounded-xl hover:bg-[#f0efe9] hover:shadow-md transition-all duration-200 group cursor-pointer border border-transparent hover:border-[#C6A14A]/30"
                  data-testid={`capability-card-${item.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#C6A14A]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C6A14A]/20 transition-colors">
                    <Check className="w-4 h-4 text-[#C6A14A]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#050816] font-semibold mb-1 group-hover:text-[#C6A14A] transition-colors">{item.title}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.shortDesc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#C6A14A] transition-colors flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>
          </div>

          {/* Subtext */}
          <p className="text-center text-sm text-muted-foreground mt-10">
            Includes integrations with platforms like Stripe, cloud storage services, messaging systems, and analytics tools.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-[#050816]">
        <div className="container mx-auto px-6 max-w-[700px] text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Ready to Build Something Real?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Let's discuss your project and create a platform that works for your business.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-bold"
              data-testid="cta-request-website"
            >
              Request a Website
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Premium Modal Overlay */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="capability-modal-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200" />
          
          {/* Modal Card */}
          <div 
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0a1628] to-[#050816] rounded-2xl shadow-2xl border border-white/10 animate-in zoom-in-95 fade-in duration-300"
            onClick={(e) => e.stopPropagation()}
            data-testid="capability-modal"
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all z-10"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Progress Indicator */}
            <div className="absolute top-6 left-6 flex gap-1.5">
              {capabilityDetails.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === activeIndex 
                      ? 'w-6 bg-[#F5C056]' 
                      : 'w-1.5 bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to item ${i + 1}`}
                />
              ))}
            </div>

            {/* Content */}
            <div className="p-8 pt-16 md:p-12 md:pt-16">
              {/* Eyebrow */}
              <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                {currentItem.eyebrow}
              </p>

              {/* Title */}
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-6">
                {currentItem.title}
              </h3>

              {/* Content Paragraphs */}
              <div className="space-y-4 mb-8">
                {currentItem.content.map((paragraph, i) => (
                  <p key={i} className="text-gray-300 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Bullet List */}
              {currentItem.bullets && (
                <div className="bg-white/5 rounded-xl p-6 mb-8">
                  <ul className="space-y-3">
                    {currentItem.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#F5C056]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-[#F5C056]" />
                        </div>
                        <span className="text-gray-200 text-sm">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Final CTA (only on last item) */}
              {isLastItem && (
                <div className="bg-gradient-to-r from-[#F5C056]/10 to-transparent rounded-xl p-6 mb-8 border border-[#F5C056]/20">
                  <p className="text-white font-serif text-xl mb-3">Ready to build something custom?</p>
                  <p className="text-gray-400 text-sm mb-4">Let's discuss your project and create a platform tailored to your business.</p>
                  <Link to="/contact" onClick={closeModal}>
                    <Button className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-11 px-6 text-sm uppercase tracking-widest font-bold">
                      Request a Quote
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-white/10">
                <button
                  onClick={goPrev}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm">Previous</span>
                </button>

                <span className="text-gray-500 text-sm">
                  {activeIndex + 1} / {capabilityDetails.length}
                </span>

                <button
                  onClick={goNext}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                >
                  <span className="text-sm">{isLastItem ? 'Back to Start' : 'Next'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteDesign;
